/**
 * 商城缓存购物车
 */
import { makeAutoObservable, toJS, runInAction } from 'mobx'
import { productMobx, restaurantCenter, RESTAURANT_EVENT, cartMobx } from '@mobx/mall'
import { pk as doPk } from '@dt/fe-utils'
import { forEach, keyBy, cloneDeep, findIndex, unionBy, find } from '@dt/fe-utils/dist/lodash'
import { MallController } from '@ctrls/mallController'

// 根据商品id 规格id和口味组返回商品pk <productId>_<scaleId>_<modifierGroupsPk> , products?: ISingleCartItem[] | undefined
export function createFkey ({
  productNo,
  scaleNo,
}: {
  productNo: string;
  scaleNo: string;
}) {
  return `${productNo}_${scaleNo}`
}

class Shopcart {
  constructor () {
    makeAutoObservable(this)
    // 结算页商品更新事件触发同步购物车商品
    restaurantCenter.on(RESTAURANT_EVENT.CART_ITEMS_CHANGED, ({ type, cartItem, isAdd }: any) => {
      if (this.isTarget) {
        if (type === 'remove') {
          this.remove(cartItem.pk)
        } else if (type === 'add') {
          this.syncCart(cartItem)
        } else if (type === 'clear') {
          this.clear()
        } else if (type === 'change') {
          if (isAdd) {
            this.add(cartItem.pk)
          } else {
            this.reduce(cartItem.pk)
          }
        }
      }
    })
  }
  /** 基础的购物车项 */
  cartItems: IMallDoProduct[] = [];

  /** 当前是否是操作购物车结算页，会根据此参数同步购物车操作 */
  isTarget: boolean = false;
  get cartItemsForFkeyMap () {
    return keyBy(this.cartItems, 'fkey')
  }
  /** 获取购物车总数量 */
  get cartTotalCnt () {
    let totalCnt = 0
    forEach(this.cartItems, ({ cnt }) => (totalCnt += cnt))
    return totalCnt
  }
  /** 初始化商城购物车 */
  init () {
    const list = MallController.restoreCartFromStorage(false) || []
    try {
      this.cartItems = list.map((item) => {
        const findProd = find(productMobx.viewProducts, prod => prod.no === item.no)
        if (findProd) {
          const findScale = find(findProd.scales, scale => scale.no === item.scaleNo)
          item = {
            ...item,
            saleCnt: findScale ? findScale.saleCnt : 0,
            scales: findProd.scales,
            saleTotalCnt: findProd.saleTotalCnt || 0,
          }
          return item
        }
        return null
      }).filter(Boolean)
    } catch (error) {}
  }
  /** 添加商品到购物车 */
  addToShopCart (doProduct: IMallDoProduct) {
    const fkey = createFkey({ productNo: doProduct.no, scaleNo: doProduct.scaleNo })
    const curIndex = findIndex(this.cartItems, (item) => item.fkey === fkey)
    if (curIndex >= 0) {
      this.cartItems[curIndex].cnt += doProduct.cnt
    } else {
      this.cartItems.push({
        ...doProduct,
        pk: '',
        spk: doProduct.pk || doPk(),
        fkey: createFkey({ productNo: doProduct.no, scaleNo: doProduct.scaleNo })
      })
    }
    restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.SHOPCART_ITEMS_CHANGED, { type: 'add', cartItems: this.cartItems })
  }

  /** 删除商品，支持cartItem也支持pk */
  remove (cart: IMallDoProduct | string | string[], fromSync = false) {
    if (Array.isArray(cart)) {
      cart.forEach(pk => this.remove(pk))
    } else {
      const index = this.cartItems.findIndex(item => item.spk === (typeof cart === 'string' ? cart : cart.pk))
      if (~index) {
        // 保证事件到达时数量是正确的，即为0
        const cpCartItem = cloneDeep(this.cartItems[index])
        cpCartItem.cnt = 0
        this.cartItems.splice(index, 1)
        restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.SHOPCART_ITEMS_CHANGED, {
          type: 'remove',
          cartItem: cpCartItem,
          fromSync
        })
        // 自动计算满送
        // this.autoFullNFree()
      }
    }
  }

  /** 递增商品数量，支持cartItem 支持pk */
  add (cart: IMallDoProduct | IMallCartItem | string) {
    const index = this.cartItems.findIndex(item => item.spk === (typeof cart === 'string' ? cart : cart.pk))
    if (~index) {
      this.cartItems[index].cnt++
      restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.SHOPCART_ITEMS_CHANGED, {
        type: 'change',
        cartItem: this.cartItems[index]
      })
    }
  }

  /** 递减商品数量，如果删除到0则删除该cartItem 支持pk */
  reduce (cart: IMallDoProduct | IMallCartItem | string) {
    const index = this.cartItems.findIndex(item => item.spk === (typeof cart === 'string' ? cart : cart.pk))
    if (~index) {
      let cartItem = this.cartItems[index]
      if (cartItem.cnt <= 1) {
        this.remove(cartItem)
        return
      } else {
        this.cartItems[index].cnt--
        restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.SHOPCART_ITEMS_CHANGED, {
          type: 'change',
          cartItem: this.cartItems[index]
        })
      }
    }
  }

  /** 清除购物车商品 */
  clear () {
    this.cartItems = []
    restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.SHOPCART_ITEMS_CHANGED, {
      type: 'clear'
    })
  }

  /** flag 当前是否是操作购物车结算页 */
  setTarget (bool: boolean = true) {
    this.isTarget = bool
  }

  /** 根据一份clone的副本还原购物车 */
  async restoreCart (cartItems: IMallDoProduct[]) {
    setTimeout(async () => {
      runInAction(() => {
        this.cartItems = cartItems
      })
    }, 100)
  }

  /** 结算页修改商品规格后同步到购物车 */
  syncCart (cart: IMallDoProduct | IMallCartItem) {
    const index = this.cartItems.findIndex(item => item.spk === (typeof cart === 'string' ? cart : cart.pk))
    if (~index) {
      this.cartItems[index] = {
        ...this.cartItems[index],
        ...cart,
        pk: '',
      }
      restaurantCenter.emit(restaurantCenter.RESTAURANT_EVENT.SHOPCART_ITEMS_CHANGED, {
        type: 'change',
        cartItem: this.cartItems[index]
      })
    }
  }

  /** 添加购物车商品到结算页 */
  addShopCartToCheckout () {
    this.setTarget()
    cartMobx.addToCart(cloneDeep(this.cartItems))
  }

  /** 获取一份当前购物车的copy */
  getCopy (isClone: boolean = true): IMallDoProduct[] {
    return (isClone ? cloneDeep(this.cartItems) : this.cartItems) as IMallDoProduct[]
  }
}

export default new Shopcart()
